import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import { UPDATE_STORE } from 'gql/mutations';
import { INSPIRED_MERCHANT_REPORT, MERCHANT_TREE_RECORD_REPORT } from 'gql/queries';
import { withNotification } from 'components/Notification';
import {
  NotificationTypes,
  apiErrorHandler,
  getPriceValue,
  SizeEnum,
} from 'utils';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from 'components/Card';
import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import { Progress } from 'components/Progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/TabsNew';
import { Gift, Leaf, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Modal } from 'components/Modal';
import {
  InspiredMerchantReportData,
  InspiredMerchantReportInput,
  InspiredMerchantReportGranularity,
} from 'types/inspiredReport';
import {
  MerchantTreeRecordReportData,
  MerchantTreeRecordReportInput,
} from 'types/treeReport';
import {
  formatExactUSD,
  formatPeriodIdentifier,
  formatBrowserTimestamp,
} from 'utils/exactDecimal';

const PAGE_LIMIT = 10;

const isValidTreeCost = (raw: string | number | null | undefined): boolean => {
  const str = String(raw ?? '').trim();
  if (!str || str === '.') return false;
  const num = Number(str);
  return !Number.isNaN(num) && Number.isFinite(num) && num >= 0;
};

const getSavedTreeCost = (rawCost?: string | number | null): string => {
  if (rawCost != null && String(rawCost).trim() !== '') {
    const costStr = String(rawCost).trim();
    if (isValidTreeCost(costStr)) {
      return costStr;
    }
  }
  return '0.75';
};

const faqItems = [
  {
    question: 'What impact add-ons does ShipAid support?',
    answer:
      'ShipAid currently offers Tree Planting and Free Gifts add-ons, allowing your store to generate real-world impact for every guaranteed order.',
    link: 'https://help.shipaid.com/en/article/does-shipaid-offer-customer-notifications-1q39rob/',
  },
  {
    question: 'How does the Tree Planting add-on work?',
    answer:
      'When enabled, orders with shipping guarantee register tree planting through TheGoodAPI, partnering with Eden Reforestation and Veritree. You can customize cost, coverage, and display in your Impact settings.',
    link: 'https://help.shipaid.com/en/article/what-does-the-shipaid-x-thegoodapi-partnership-offer-y4ecs/',
  },
  {
    question: 'How does the Free Gifts add-on work?',
    answer:
      'Customers who opt into Delivery Guarantee receive a free donation to a charity of their choosing. Platform fees apply per agreement. Inspired donor network funds customer charitable gifts.',
    link: 'https://help.shipaid.com/en/article/what-does-the-shipaid-free-gifts-add-on-offer-c4qqpt/',
  },
  {
    question: 'How do I customize Impact settings for my store?',
    answer:
      'Impact add-ons can be enabled, disabled, or configured directly in the Impact Settings panel — pricing, messaging, coverage, and customer presentation are all adjustable.',
    link: 'https://help.shipaid.com/en/collections/1131282-impact',
  },
  {
    question: "Can I use ShipAid's Impact widgets or marketing assets?",
    answer:
      'Yes — you can embed Impact widgets and download marketing assets to showcase your sustainability or charitable initiatives to customers.',
    link: 'https://help.shipaid.com/en/collections/1131282-impact',
  },
  {
    question: 'Where can I access Impact performance data?',
    answer:
      'Historical customer gifting activity, period trends, supported causes, and verified charitable redemptions funded by Inspired are visible in the Inspired Impact Report above.',
    link: 'https://help.shipaid.com/en/collections/1131282-impact',
  },
];

const Impact = withNotification(
  ({ showNotification }: { showNotification: (...params: any) => void; [key: string]: any }) => {
    const [greenProtectionEnabled, setGreenProtectionEnabled] = useState(false);
    const [freeGiftsEnabled, setFreeGiftsEnabled] = useState(false);
    const [savingGreenStoreIds, setSavingGreenStoreIds] = useState<Record<string, boolean>>({});
    const [savingFreeGiftsStoreIds, setSavingFreeGiftsStoreIds] = useState<Record<string, boolean>>({});

    const [treeCost, setTreeCost] = useState('0.75');
    const [baseCost] = useState('0.75');
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
    const [showTreeTooltip, setShowTreeTooltip] = useState(false);

    // Reporting State
    const [granularity, setGranularity] = useState<InspiredMerchantReportGranularity>('month');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [detailOffset, setDetailOffset] = useState<number>(0);

    const { storeId, storeProperties, refetchStoreProperties } = useStore();
    const activeStoreIdRef = useRef(storeId);
    activeStoreIdRef.current = storeId;

    const isStoreMatch = Boolean(
      storeId && storeProperties && storeProperties.id === storeId,
    );

    const isTreePlantingSaving = Boolean(storeId && savingGreenStoreIds[storeId]);
    const isFreeGiftsSaving = Boolean(storeId && savingFreeGiftsStoreIds[storeId]);

    // Hydrate settings whenever storeId matches storeProperties.id
    useEffect(() => {
      if (isStoreMatch) {
        setGreenProtectionEnabled(
          Boolean(storeProperties?.greenProtection?.enabled),
        );
        setFreeGiftsEnabled(Boolean(storeProperties?.freeGifts?.enabled));
        setTreeCost(
          getSavedTreeCost(storeProperties?.greenProtection?.treePlantingCost),
        );
      } else {
        setGreenProtectionEnabled(false);
        setFreeGiftsEnabled(false);
        setTreeCost('0.75');
      }
    }, [
      isStoreMatch,
      storeProperties?.id,
      storeProperties?.greenProtection?.enabled,
      storeProperties?.freeGifts?.enabled,
      storeProperties?.greenProtection?.treePlantingCost,
    ]);

    // Reset date filters and pagination offset whenever storeId changes
    const [prevStoreId, setPrevStoreId] = useState(storeId);
    if (storeId !== prevStoreId) {
      setPrevStoreId(storeId);
      setFromDate('');
      setToDate('');
      setDetailOffset(0);
    }

    const effectiveGreenProtectionEnabled = isStoreMatch
      ? greenProtectionEnabled
      : false;
    const effectiveFreeGiftsEnabled = isStoreMatch ? freeGiftsEnabled : false;
    const isMountedRef = useRef(true);
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (!showTreeTooltip) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowTreeTooltip(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [showTreeTooltip]);

    // Do NOT pass onError to useMutation to avoid obsolete un-scoped notifications
    const [updateStore] = useMutation(UPDATE_STORE);

    const handleGreenProtectionSwitch = (enabled: boolean) => {
      if (!storeId || !isStoreMatch || isTreePlantingSaving) return;
      const saveStoreId: string = storeId;
      const previous = greenProtectionEnabled;
      setGreenProtectionEnabled(enabled);

      const savedCost = getSavedTreeCost(
        storeProperties?.greenProtection?.treePlantingCost,
      );
      let effectiveTreeCost = treeCost;
      if (!isValidTreeCost(treeCost)) {
        effectiveTreeCost = savedCost;
        setTreeCost(savedCost);
      }

      setSavingGreenStoreIds((prev) => ({ ...prev, [saveStoreId]: true }));

      const extraGreen =
        storeProperties?.greenProtection &&
        typeof storeProperties.greenProtection === 'object'
          ? storeProperties.greenProtection
          : {};

      const treePlantingCost = getPriceValue(effectiveTreeCost);

      updateStore({
        variables: {
          storeId: saveStoreId,
          set: {
            greenProtection: {
              ...extraGreen,
              enabled,
              treePlantingCost,
            },
          },
        },
      })
        .then((res) => {
          if (!isMountedRef.current) return;
          if (
            activeStoreIdRef.current !== saveStoreId ||
            res?.data?.updateStore?.id !== saveStoreId
          ) {
            if (activeStoreIdRef.current === saveStoreId) {
              setGreenProtectionEnabled(previous);
            }
            return;
          }
          showNotification(
            NotificationTypes.success,
            'Impact settings updated',
          );
          if (refetchStoreProperties) {
            refetchStoreProperties().catch(() => {});
          }
        })
        .catch((error) => {
          if (!isMountedRef.current) return;
          if (activeStoreIdRef.current === saveStoreId) {
            setGreenProtectionEnabled(previous);
            const newError = apiErrorHandler(error);
            showNotification(
              NotificationTypes.error,
              newError?.message || 'Impact settings not updated',
            );
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setSavingGreenStoreIds((prev) => ({ ...prev, [saveStoreId]: false }));
          }
        });
    };

    const handleFreeGiftsSwitch = (enabled: boolean) => {
      if (!storeId || !isStoreMatch || isFreeGiftsSaving) return;
      const saveStoreId: string = storeId;
      const previous = freeGiftsEnabled;
      setFreeGiftsEnabled(enabled);
      setSavingFreeGiftsStoreIds((prev) => ({ ...prev, [saveStoreId]: true }));

      const extraFields =
        storeProperties?.freeGifts &&
        typeof storeProperties.freeGifts === 'object'
          ? storeProperties.freeGifts
          : {};

      updateStore({
        variables: {
          storeId: saveStoreId,
          set: {
            freeGifts: {
              ...extraFields,
              enabled,
            },
          },
        },
      })
        .then((res) => {
          if (!isMountedRef.current) return;
          if (
            activeStoreIdRef.current !== saveStoreId ||
            res?.data?.updateStore?.id !== saveStoreId
          ) {
            if (activeStoreIdRef.current === saveStoreId) {
              setFreeGiftsEnabled(previous);
            }
            return;
          }
          showNotification(
            NotificationTypes.success,
            'Impact settings updated',
          );
          if (refetchStoreProperties) {
            refetchStoreProperties().catch(() => {});
          }
        })
        .catch((error) => {
          if (!isMountedRef.current) return;
          if (activeStoreIdRef.current === saveStoreId) {
            setFreeGiftsEnabled(previous);
            const newError = apiErrorHandler(error);
            showNotification(
              NotificationTypes.error,
              newError?.message || 'Impact settings not updated',
            );
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setSavingFreeGiftsStoreIds((prev) => ({ ...prev, [saveStoreId]: false }));
          }
        });
    };

    const handleTreeCost = () => {
      if (!storeId || !isStoreMatch || isTreePlantingSaving) return;

      const savedCost = getSavedTreeCost(
        storeProperties?.greenProtection?.treePlantingCost,
      );
      if (!isValidTreeCost(treeCost)) {
        setTreeCost(savedCost);
        return;
      }

      const saveStoreId: string = storeId;
      setSavingGreenStoreIds((prev) => ({ ...prev, [saveStoreId]: true }));

      const extraGreen =
        storeProperties?.greenProtection &&
        typeof storeProperties.greenProtection === 'object'
          ? storeProperties.greenProtection
          : {};

      const treePlantingCost = getPriceValue(treeCost);

      updateStore({
        variables: {
          storeId: saveStoreId,
          set: {
            greenProtection: {
              ...extraGreen,
              enabled: effectiveGreenProtectionEnabled,
              treePlantingCost,
            },
          },
        },
      })
        .then((res) => {
          if (!isMountedRef.current) return;
          if (
            activeStoreIdRef.current !== saveStoreId ||
            res?.data?.updateStore?.id !== saveStoreId
          ) {
            return;
          }
          showNotification(
            NotificationTypes.success,
            'Impact settings updated',
          );
          if (refetchStoreProperties) {
            refetchStoreProperties().catch(() => {});
          }
        })
        .catch((error) => {
          if (!isMountedRef.current) return;
          if (activeStoreIdRef.current === saveStoreId) {
            setTreeCost(savedCost);
            const newError = apiErrorHandler(error);
            showNotification(
              NotificationTypes.error,
              newError?.message || 'Impact settings not updated',
            );
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setSavingGreenStoreIds((prev) => ({ ...prev, [saveStoreId]: false }));
          }
        });
    };

    const handleTreePriceChange = (value: string) => {
      const numericValue = value.replace(/[^0-9.]/g, '');
      if (/^\d*\.?\d*$/.test(numericValue)) {
        setTreeCost(numericValue);
      }
    };

    const enabledCount =
      (effectiveGreenProtectionEnabled ? 1 : 0) +
      (effectiveFreeGiftsEnabled ? 1 : 0);
    const progress = (enabledCount / 2) * 100;

    // Reporting Query with explicit UTC instant bounds
    const reportQueryInput: InspiredMerchantReportInput = useMemo(
      () => ({
        storeId: storeId || '',
        granularity,
        from: fromDate.trim() ? `${fromDate.trim()}T00:00:00.000Z` : null,
        to: toDate.trim() ? `${toDate.trim()}T23:59:59.999Z` : null,
        detailLimit: PAGE_LIMIT,
        detailOffset,
      }),
      [storeId, granularity, fromDate, toDate, detailOffset],
    );

    const {
      data: reportQueryData,
      loading: reportLoading,
      error: reportError,
      refetch: refetchReport,
    } = useQuery<InspiredMerchantReportData, { input: InspiredMerchantReportInput }>(
      INSPIRED_MERCHANT_REPORT,
      {
        variables: { input: reportQueryInput },
        skip: !storeId,
        fetchPolicy: 'cache-and-network',
      },
    );

    // Guard against stale Apollo cache retention for another store
    const rawReport = reportQueryData?.inspiredMerchantReport;
    const isReportMatch = Boolean(
      rawReport && storeId && rawReport.storeId === storeId,
    );
    const report = isReportMatch ? rawReport : null;
    const isCurrencyValid = !report || report.currency === 'USD';

    // Tree Planting Record Reporting Query
    const treeQueryInput: MerchantTreeRecordReportInput = useMemo(
      () => ({
        storeId: storeId || '',
        from: fromDate.trim() ? `${fromDate.trim()}T00:00:00.000Z` : null,
        to: toDate.trim() ? `${toDate.trim()}T23:59:59.999Z` : null,
      }),
      [storeId, fromDate, toDate],
    );

    const {
      data: treeQueryData,
      loading: treeReportLoading,
      error: treeReportError,
      refetch: refetchTreeReport,
    } = useQuery<MerchantTreeRecordReportData, { input: MerchantTreeRecordReportInput }>(
      MERCHANT_TREE_RECORD_REPORT,
      {
        variables: { input: treeQueryInput },
        skip: !storeId,
        fetchPolicy: 'cache-and-network',
      },
    );

    // Guard against stale Apollo cache retention for another store, range, or unsupported date basis
    const requestedFrom = treeQueryInput.from ?? null;
    const requestedTo = treeQueryInput.to ?? null;
    const rawTreeReport = treeQueryData?.merchantTreeRecordReport;
    const isTreeReportMatch = Boolean(
      rawTreeReport &&
      storeId &&
      rawTreeReport.storeId === storeId &&
      (rawTreeReport.from ?? null) === requestedFrom &&
      (rawTreeReport.to ?? null) === requestedTo &&
      rawTreeReport.dateBasis === 'order_created_at',
    );
    const treeReport = isTreeReportMatch ? rawTreeReport : null;
    const isTreeAvailable = Boolean(treeReport && treeReport.status === 'available');

    const handleRetryTreeReport = () => {
      if (refetchTreeReport) {
        refetchTreeReport().catch(() => {});
      }
    };

    const renderRecordMetric = (count: number | null | undefined) => {
      if (isTreeAvailable && count !== null && count !== undefined) {
        return count.toLocaleString();
      }
      if (treeReportLoading) {
        return '—';
      }
      return 'Unavailable';
    };

    const handleGranularityChange = (newGranularity: InspiredMerchantReportGranularity) => {
      setGranularity(newGranularity);
      setDetailOffset(0);
    };

    const handleFromDateChange = (val: string) => {
      setFromDate(val);
      setDetailOffset(0);
    };

    const handleToDateChange = (val: string) => {
      setToDate(val);
      setDetailOffset(0);
    };

    const handleNextPage = () => {
      if (report?.recentActivity?.pagination?.hasMore) {
        setDetailOffset(detailOffset + PAGE_LIMIT);
      }
    };

    const handlePrevPage = () => {
      setDetailOffset(Math.max(0, detailOffset - PAGE_LIMIT));
    };

    return (
      <SettingsViewWrapper activeNav="impact">
        <div className="pb-8">
          <div className="max-w-7xl mx-auto mb-12 px-4 sm:px-6 md:px-8 pt-6 space-y-8">
            {/* Header */}
            <div className="space-y-2">
              <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
                Impact
              </h1>
              <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
                Create real-world impact with every guaranteed order. Choose how
                your store gives back — enable one or both options.
              </p>
              <div className="max-w-sm">
                <Progress value={progress} />
                <p className="text-sm text-neutral-500 mt-1">
                  {enabledCount} of 2 Impact add-ons enabled
                </p>
              </div>
            </div>

            {/* Neutral Shared Report Filter Area */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span className="text-sm font-medium text-gray-700">
                  Report Date Range (UTC):
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center space-x-1.5">
                    <label htmlFor="report-from-date" className="text-xs text-gray-600 font-medium">From:</label>
                    <input
                      id="report-from-date"
                      type="date"
                      value={fromDate}
                      onChange={(e) => handleFromDateChange(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                    />
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <label htmlFor="report-to-date" className="text-xs text-gray-600 font-medium">To:</label>
                    <input
                      id="report-to-date"
                      type="date"
                      value={toDate}
                      onChange={(e) => handleToDateChange(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                    />
                  </div>
                  {(fromDate || toDate) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                        setDetailOffset(0);
                      }}
                      className="text-xs text-blue-600 underline hover:text-blue-800"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Dates are filtered in UTC and apply to both Tree Planting and Inspired reports. Summary date filters select period start; recent gift activity filters select gift creation date.
              </p>
            </div>

            {/* Tree Planting Program Section */}
            <section aria-labelledby="tree-planting-heading" className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Leaf className={`h-6 w-6 ${effectiveGreenProtectionEnabled ? 'text-green-600' : 'text-neutral-400'} flex-shrink-0`} />
                  <div>
                    <h2 id="tree-planting-heading" className="text-xl font-bold text-gray-900">
                      Tree Planting
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Register tree planting for eligible guaranteed orders through TheGoodAPI, partnering with Eden &amp; Veritree.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    isTreePlantingSaving
                      ? 'bg-amber-100 text-amber-800'
                      : !isStoreMatch
                      ? 'bg-neutral-100 text-neutral-600'
                      : effectiveGreenProtectionEnabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-neutral-100 text-neutral-600'
                  }`}>
                    {isTreePlantingSaving
                      ? 'Saving'
                      : !isStoreMatch
                      ? 'Unavailable'
                      : effectiveGreenProtectionEnabled
                      ? 'Enabled'
                      : 'Disabled'}
                  </span>
                  <Toggle
                    isEnabled={effectiveGreenProtectionEnabled}
                    onChange={handleGreenProtectionSwitch}
                    disabled={!isStoreMatch || isTreePlantingSaving}
                    ariaLabel="Toggle Tree Planting Program Analytics"
                    activeClassNames="bg-green-600"
                    knobActiveClassNames="bg-white"
                    knobInactiveClassNames="bg-neutral-400"
                    className={effectiveGreenProtectionEnabled ? '!bg-green-600' : '!bg-neutral-200'}
                    isSmall
                  />
                </div>
              </div>

              {/* Tree Planting Analytics Card */}
              <div className={`bg-white border ${effectiveGreenProtectionEnabled ? 'border-green-200' : 'border-neutral-200'} rounded-xl p-5 shadow-sm space-y-3`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Leaf className={`h-5 w-5 ${effectiveGreenProtectionEnabled ? 'text-green-600' : 'text-neutral-400'} flex-shrink-0`} />
                    <h3 className="text-base font-semibold text-gray-900">Tree Planting Analytics</h3>
                    <div className="relative inline-flex items-center">
                      <button
                        type="button"
                        aria-label="More information about tree planting analytics"
                        aria-describedby="tree-analytics-tooltip"
                        onFocus={() => setShowTreeTooltip(true)}
                        onBlur={() => setShowTreeTooltip(false)}
                        onMouseEnter={() => setShowTreeTooltip(true)}
                        onMouseLeave={() => setShowTreeTooltip(false)}
                        onTouchStart={() => setShowTreeTooltip((prev) => !prev)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setShowTreeTooltip(false);
                          }
                        }}
                        className="text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-full p-0.5"
                      >
                        <HelpCircle className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <div
                        id="tree-analytics-tooltip"
                        role="tooltip"
                        aria-hidden={!showTreeTooltip}
                        className={`absolute bottom-full mb-1.5 right-0 translate-x-0 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 w-48 sm:w-auto sm:whitespace-nowrap max-w-[calc(100vw-2rem)] px-2.5 py-1 text-xs font-normal text-white bg-gray-900 rounded shadow-md z-20 pointer-events-none transition-opacity duration-150 text-center ${
                          showTreeTooltip ? 'opacity-100 visible' : 'opacity-0 invisible'
                        }`}
                      >
                        Order records, not verified planting totals.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {treeReportLoading ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                        Loading tree reporting...
                      </span>
                    ) : !isTreeAvailable ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                        {"Tree reporting isn't available for this store yet"}
                      </span>
                    ) : null}
                  </div>
                </div>
                {treeReportError && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                    <span>Unable to load tree report</span>
                    <button
                      type="button"
                      onClick={handleRetryTreeReport}
                      className="inline-flex items-center px-2.5 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Retry tree report
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                  <div className={`${effectiveGreenProtectionEnabled ? 'bg-green-50/50 border-green-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-lg p-3 min-w-0 transition-colors`}>
                    <p className={`text-xs font-medium ${effectiveGreenProtectionEnabled ? 'text-gray-600' : 'text-neutral-500'}`}>Registered orders</p>
                    <p className={`text-sm font-semibold ${effectiveGreenProtectionEnabled ? 'text-gray-700' : 'text-neutral-700'} mt-1 break-all`}>
                      {renderRecordMetric(treeReport?.recordedOrderCount)}
                    </p>
                  </div>
                  <div className={`${effectiveGreenProtectionEnabled ? 'bg-green-50/50 border-green-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-lg p-3 min-w-0 transition-colors`}>
                    <p className={`text-xs font-medium ${effectiveGreenProtectionEnabled ? 'text-gray-600' : 'text-neutral-500'}`}>Refunded orders</p>
                    <p className={`text-sm font-semibold ${effectiveGreenProtectionEnabled ? 'text-gray-700' : 'text-neutral-700'} mt-1 break-all`}>
                      {renderRecordMetric(treeReport?.refundedRecordedOrderCount)}
                    </p>
                  </div>
                  <div className={`${effectiveGreenProtectionEnabled ? 'bg-green-50/50 border-green-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-lg p-3 min-w-0 transition-colors`}>
                    <p className={`text-xs font-medium ${effectiveGreenProtectionEnabled ? 'text-gray-600' : 'text-neutral-500'}`}>Active orders</p>
                    <p className={`text-sm font-semibold ${effectiveGreenProtectionEnabled ? 'text-gray-700' : 'text-neutral-700'} mt-1 break-all`}>
                      {renderRecordMetric(treeReport?.activeRecordedOrderCount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tree Planting Add-On Configuration */}
              <Card className="border-green-200 shadow-sm hover:border-green-300 transition-colors">
                <CardHeader className="flex items-center justify-between flex-row">
                  <div className="flex items-center gap-2">
                    <Leaf className="h-5 w-5 text-green-600" />
                    <CardTitle>Tree Planting Add-On</CardTitle>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      isTreePlantingSaving
                        ? 'bg-amber-100 text-amber-800'
                        : !isStoreMatch
                        ? 'bg-neutral-100 text-neutral-600'
                        : effectiveGreenProtectionEnabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {isTreePlantingSaving
                        ? 'Saving'
                        : !isStoreMatch
                        ? 'Unavailable'
                        : effectiveGreenProtectionEnabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </span>
                  </div>
                  <Toggle
                    isEnabled={effectiveGreenProtectionEnabled}
                    onChange={handleGreenProtectionSwitch}
                    disabled={!isStoreMatch || isTreePlantingSaving}
                    ariaLabel="Toggle Tree Planting Add-On"
                    activeClassNames="bg-green-600"
                    knobActiveClassNames="bg-white"
                    knobInactiveClassNames="bg-neutral-400"
                    className={effectiveGreenProtectionEnabled ? '!bg-green-600' : '!bg-neutral-200'}
                    isSmall
                  />
                </CardHeader>
                <CardDescription className="px-6 text-sm text-neutral-600">
                  Register tree planting for eligible guaranteed orders through
                  TheGoodAPI, partnering with Eden &amp; Veritree.
                </CardDescription>
                <CardContent className="pt-4 space-y-4">
                  <Tabs defaultValue="setup">
                    <TabsList className="!h-auto max-w-full flex-wrap gap-1">
                      <TabsTrigger value="setup" className="!px-2 sm:!px-3">Setup &amp; Pricing</TabsTrigger>
                      <TabsTrigger value="resources" className="!px-2 sm:!px-3">Resources</TabsTrigger>
                    </TabsList>

                    <TabsContent value="setup" className="pt-4 space-y-4">
                      <div className="flex items-center justify-between max-w-md">
                        <div>
                          <label htmlFor="tree-planting-cost" className="text-sm font-medium text-neutral-800">
                            Cost per Tree Planted
                          </label>
                          <p className="text-xs text-neutral-500">
                            Base cost: ${baseCost} | You can adjust what you charge
                            or absorb
                          </p>
                        </div>
                        <div className="w-28 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">
                            $
                          </span>
                          <input
                            id="tree-planting-cost"
                            type="text"
                            value={treeCost}
                            disabled={!isStoreMatch || isTreePlantingSaving}
                            onChange={(e) => handleTreePriceChange(e.target.value)}
                            onBlur={handleTreeCost}
                            className="w-full pl-7 pr-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="resources" className="pt-4 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-green-300 bg-white text-green-600 hover:bg-green-50 hover:border-green-400"
                          onClick={() =>
                            window.open(
                              'https://help.shipaid.com/en/article/what-does-the-shipaid-x-thegoodapi-partnership-offer-y4ecs/',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          TheGoodAPI Partnership FAQ
                        </Button>
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-green-300 bg-white text-green-600 hover:bg-green-50 hover:border-green-400"
                          onClick={() =>
                            window.open(
                              'https://help.shipaid.com/en/article/how-do-i-showcase-my-tree-planting-efforts-to-customers-z6d22/',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          Showcase Tree Planting
                        </Button>
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-green-300 bg-white text-green-600 hover:bg-green-50 hover:border-green-400"
                          onClick={() =>
                            window.open(
                              'https://guides.shipaid.com/demo/cmgb2vynd3wyv2nomrzpxn5xj',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          Watch Overview Video
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </section>

            {/* Inspired Program Section */}
            <section aria-labelledby="inspired-program-heading" className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Gift className={`h-6 w-6 ${effectiveFreeGiftsEnabled ? 'text-purple-600' : 'text-neutral-400'} flex-shrink-0`} />
                  <div>
                    <h2 id="inspired-program-heading" className="text-xl font-bold text-gray-900">
                      Inspired
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Historical customer gifting activity and verified charitable redemptions funded by Inspired.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {report && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      {Boolean(
                        report.freshness?.isStale &&
                          (report.freshness?.earliestRefreshedAt ||
                            report.freshness?.refreshedAt),
                      ) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Delayed (&gt;48h)
                        </span>
                      )}
                      {report.freshness?.earliestRefreshedAt || report.freshness?.refreshedAt ? (
                        <span className="text-gray-500">
                          Earliest: {formatBrowserTimestamp(report.freshness.earliestRefreshedAt || report.freshness.refreshedAt)}
                          {report.freshness.latestRefreshedAt && ` · Latest: ${formatBrowserTimestamp(report.freshness.latestRefreshedAt)}`}
                        </span>
                      ) : (
                        <span className="text-gray-500">Freshness: Unknown</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      isFreeGiftsSaving
                        ? 'bg-amber-100 text-amber-800'
                        : !isStoreMatch
                        ? 'bg-neutral-100 text-neutral-600'
                        : effectiveFreeGiftsEnabled
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {isFreeGiftsSaving
                        ? 'Saving'
                        : !isStoreMatch
                        ? 'Unavailable'
                        : effectiveFreeGiftsEnabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </span>
                    <Toggle
                      isEnabled={effectiveFreeGiftsEnabled}
                      onChange={handleFreeGiftsSwitch}
                      disabled={!isStoreMatch || isFreeGiftsSaving}
                      ariaLabel="Toggle Free Gifts Program Analytics"
                      activeClassNames="bg-purple-600"
                      knobActiveClassNames="bg-white"
                      knobInactiveClassNames="bg-neutral-400"
                      className={effectiveFreeGiftsEnabled ? '!bg-purple-600' : '!bg-neutral-200'}
                      isSmall
                    />
                  </div>
                </div>
              </div>

              {/* Granularity Control */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Granularity:</span>
                  <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Report granularity">
                    <button
                      type="button"
                      aria-pressed={granularity === 'month'}
                      onClick={() => handleGranularityChange('month')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-l-lg border ${
                        granularity === 'month'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      aria-pressed={granularity === 'week'}
                      onClick={() => handleGranularityChange('week')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-r-lg border-t border-b border-r ${
                        granularity === 'week'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Weekly
                    </button>
                  </div>
                </div>
              </div>

              {/* Report States Rendering */}
              {reportLoading && !report ? (
                <div
                  data-testid="report-loading-skeleton"
                  className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-200 animate-pulse"
                >
                  <svg
                    className="h-6 w-6 mx-auto animate-spin text-gray-400 mb-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  <p className="text-sm">Loading Inspired Impact Report...</p>
                </div>
              ) : reportError || !isCurrencyValid ? (
                <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-center space-y-3">
                  <div className="flex items-center justify-center text-red-600 gap-1.5">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-base font-semibold text-red-800">
                      Unable to Load Inspired Report
                    </h3>
                  </div>
                  <p className="text-sm text-red-600">
                    {reportError?.message || (!isCurrencyValid ? 'Currency must be USD' : 'There was an error loading the report.')}
                  </p>
                  <Button
                    variant="outlined"
                    size={SizeEnum.sm}
                    onClick={() => refetchReport()}
                  >
                    Retry
                  </Button>
                </div>
              ) : report?.status === 'unconfigured' ? (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-xl text-center space-y-2">
                  <h3 className="text-base font-semibold text-gray-800">
                    Inspired Reporting Unconfigured
                  </h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    {report.message || 'Store is not configured for Inspired gifting reports.'}
                  </p>
                </div>
              ) : report?.status === 'unavailable' ? (
                <div className="p-8 bg-amber-50 border border-amber-200 rounded-xl text-center space-y-3">
                  <h3 className="text-base font-semibold text-amber-800">
                    Inspired Reporting Unavailable
                  </h3>
                  <p className="text-sm text-amber-700 max-w-md mx-auto">
                    {report.message || 'Inspired reporting is temporarily unavailable.'}
                  </p>
                  <Button
                    variant="outlined"
                    size={SizeEnum.sm}
                    onClick={() => refetchReport()}
                  >
                    Retry
                  </Button>
                </div>
              ) : report?.status === 'available' && report.summary ? (
                <div className="space-y-8">
                  {/* Summary Metric Cards */}
                  <div
                    data-testid="report-summary"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <div className={`${effectiveFreeGiftsEnabled ? 'bg-purple-50/20 border-purple-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-xl p-5 shadow-sm min-w-0 transition-colors`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-medium ${effectiveFreeGiftsEnabled ? 'text-purple-900' : 'text-neutral-500'} uppercase tracking-wider`}>
                          Gifts Created
                        </p>
                        <Gift className={`h-4 w-4 ${effectiveFreeGiftsEnabled ? 'text-purple-500' : 'text-neutral-400'}`} aria-hidden="true" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2 break-all">
                        {report.summary.totalGiftCount}
                      </p>
                    </div>

                    <div className={`${effectiveFreeGiftsEnabled ? 'bg-purple-50/20 border-purple-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-xl p-5 shadow-sm min-w-0 transition-colors`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-medium ${effectiveFreeGiftsEnabled ? 'text-purple-900' : 'text-neutral-500'} uppercase tracking-wider`}>
                          Reported Redemptions
                        </p>
                        <Gift className={`h-4 w-4 ${effectiveFreeGiftsEnabled ? 'text-purple-500' : 'text-neutral-400'}`} aria-hidden="true" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2 break-all">
                        {report.summary.totalRedemptionCount}
                      </p>
                    </div>

                    <div className={`${effectiveFreeGiftsEnabled ? 'bg-purple-50/20 border-purple-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-xl p-5 shadow-sm min-w-0 transition-colors`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-medium ${effectiveFreeGiftsEnabled ? 'text-purple-900' : 'text-neutral-500'} uppercase tracking-wider`}>
                          Reported Gift Value
                        </p>
                        <Gift className={`h-4 w-4 ${effectiveFreeGiftsEnabled ? 'text-purple-500' : 'text-neutral-400'}`} aria-hidden="true" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2 break-all">
                        {formatExactUSD(report.summary.totalGiftAmount)}
                      </p>
                    </div>

                    <div className={`${effectiveFreeGiftsEnabled ? 'bg-purple-50/20 border-purple-200' : 'bg-neutral-50/80 border-neutral-200'} border rounded-xl p-5 shadow-sm min-w-0 transition-colors`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-medium ${effectiveFreeGiftsEnabled ? 'text-purple-900' : 'text-neutral-500'} uppercase tracking-wider`}>
                          Reported Redeemed Value
                        </p>
                        <Gift className={`h-4 w-4 ${effectiveFreeGiftsEnabled ? 'text-purple-500' : 'text-neutral-400'}`} aria-hidden="true" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2 break-all">
                        {formatExactUSD(report.summary.totalRedeemedAmount)}
                      </p>
                    </div>
                  </div>

                  {report.summary.totalGiftCount === 0 &&
                    report.summary.totalRedemptionCount === 0 &&
                    (!report.recentActivity?.items || report.recentActivity.items.length === 0) && (
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm text-gray-600">
                        No gift activity reported for this period
                      </div>
                    )}

                  {/* Period Trends */}
                  {report.summary.periods && report.summary.periods.length > 0 && (
                    <div data-testid="report-periods" className="space-y-3">
                      <h3 className="text-base font-semibold text-gray-900">
                        Trends by Period
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 font-medium text-gray-700">Period</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Gifts</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Gift Value</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Redemptions</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Redeemed Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {report.summary.periods.map((p, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-900">
                                  {formatPeriodIdentifier(p.periodStart, granularity)}
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700">{p.giftCount}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatExactUSD(p.giftAmount)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{p.redemptionCount}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatExactUSD(p.redeemedAmount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Causes & Charities Breakdown */}
                  {report.summary.causes && report.summary.causes.length > 0 && (
                    <div data-testid="report-causes" className="space-y-3">
                      <h3 className="text-base font-semibold text-gray-900">
                        Charities and Causes Supported
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 font-medium text-gray-700">Charity / Cause</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Gifts</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Gift Value</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Redemptions</th>
                              <th className="px-4 py-3 font-medium text-gray-700 text-right">Redeemed Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {report.summary.causes.map((c, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  {c.isPendingRedemption ? (
                                    <span className="text-gray-500 italic">
                                      Pending Customer Selection
                                    </span>
                                  ) : (
                                    <span>
                                      {c.charityName || 'General Charity'}{' '}
                                      {c.causeName && (
                                        <span className="text-gray-500 font-normal">
                                          ({c.causeName})
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700">{c.giftCount}</td>
                                <td className="px-4 py-3 text-right text-gray-700">
                                  {formatExactUSD(c.giftAmount)}
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700">{c.redemptionCount}</td>
                                <td className="px-4 py-3 text-right text-gray-700">
                                  {formatExactUSD(c.redeemedAmount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Recent Activity Table */}
                  {report.recentActivity && (
                    <div data-testid="report-activity" className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold text-gray-900">
                          Recent Gift Activity
                        </h3>
                        <span className="text-xs text-gray-500">
                          Showing {report.recentActivity.pagination.count} gifts
                        </span>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 font-medium text-gray-700">Reference</th>
                              <th className="px-4 py-3 font-medium text-gray-700">Order</th>
                              <th className="px-4 py-3 font-medium text-gray-700">Amount</th>
                              <th className="px-4 py-3 font-medium text-gray-700">Status</th>
                              <th className="px-4 py-3 font-medium text-gray-700">Charity / Cause</th>
                              <th className="px-4 py-3 font-medium text-gray-700">Created</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {report.recentActivity.items.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                                  No recent gift activity found
                                </td>
                              </tr>
                            ) : (
                              report.recentActivity.items.map((item, idx) => (
                                <tr key={item.reference || idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                    {item.reference}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    {item.orderNumber ? item.orderNumber : '—'}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900 font-medium">
                                    {formatExactUSD(item.giftAmount)}
                                  </td>
                                  <td className="px-4 py-3">
                                    {item.redeemedAt ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                        Redeemed
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                        Unredeemed
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700">
                                    {item.charityName ? (
                                      <span>
                                        {item.charityName}{' '}
                                        {item.causeName && (
                                          <span className="text-gray-500 text-xs">
                                            ({item.causeName})
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 italic">Pending</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-xs text-gray-500">
                                    {formatBrowserTimestamp(item.createdAt)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>

                        {/* Pagination Footer */}
                        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                          <Button
                            variant="outlined"
                            size={SizeEnum.sm}
                            disabled={detailOffset === 0}
                            onClick={handlePrevPage}
                          >
                            Previous
                          </Button>
                          <span className="text-xs text-gray-500">
                            Offset: {detailOffset}
                          </span>
                          <Button
                            variant="outlined"
                            size={SizeEnum.sm}
                            disabled={!report.recentActivity.pagination.hasMore}
                            onClick={handleNextPage}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Free Gifts Add-On Configuration */}
              <Card className="border-purple-200 shadow-sm hover:border-purple-300 transition-colors">
                <CardHeader className="flex items-center justify-between flex-row">
                  <div className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-purple-600" />
                    <CardTitle>Free Gifts Add-On</CardTitle>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      isFreeGiftsSaving
                        ? 'bg-amber-100 text-amber-800'
                        : !isStoreMatch
                        ? 'bg-neutral-100 text-neutral-600'
                        : effectiveFreeGiftsEnabled
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {isFreeGiftsSaving
                        ? 'Saving'
                        : !isStoreMatch
                        ? 'Unavailable'
                        : effectiveFreeGiftsEnabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </span>
                  </div>
                  <Toggle
                    isEnabled={effectiveFreeGiftsEnabled}
                    onChange={handleFreeGiftsSwitch}
                    disabled={!isStoreMatch || isFreeGiftsSaving}
                    ariaLabel="Toggle Free Gifts Add-On"
                    activeClassNames="bg-purple-600"
                    knobActiveClassNames="bg-white"
                    knobInactiveClassNames="bg-neutral-400"
                    className={effectiveFreeGiftsEnabled ? '!bg-purple-600' : '!bg-neutral-200'}
                    isSmall
                  />
                </CardHeader>
                <CardDescription className="px-6 text-sm text-neutral-600">
                  Platform fees apply per agreement. Inspired donor network funds customer charitable gifts.
                  After checkout, customers receive a co-branded email inviting them to give to charity.
                </CardDescription>
                <CardContent className="pt-4 space-y-4">
                  <Tabs defaultValue="how">
                    <TabsList className="!h-auto max-w-full flex-wrap gap-1">
                      <TabsTrigger value="how" className="!px-2 sm:!px-3">How It Works</TabsTrigger>
                      <TabsTrigger value="resources" className="!px-2 sm:!px-3">Resources</TabsTrigger>
                    </TabsList>

                    <TabsContent value="how" className="pt-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-purple-600">
                              1
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">
                            Customer opts into Delivery Guarantee at checkout
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-purple-600">
                              2
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">
                            They receive a charity donation invitation to give to a cause they choose
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-purple-600">
                              3
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">
                            Platform fees apply per agreement. Inspired donor network funds customer charitable gifts.
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-purple-600">
                              4
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">
                            Charities are updated regularly on Inspired&apos;s Causes page
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-purple-600">
                              5
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">
                            You can track gift engagement in the report above
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-purple-300 bg-white text-purple-600 hover:bg-purple-50 hover:border-purple-400"
                          onClick={() => setShowEmailPreviewModal(true)}
                        >
                          Preview Customer Email
                        </Button>
                      </div>
                    </TabsContent>

                    <TabsContent value="resources" className="pt-4 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-purple-300 bg-white text-purple-600 hover:bg-purple-50 hover:border-purple-400"
                          onClick={() =>
                            window.open(
                              'https://help.shipaid.com/en/article/what-does-the-shipaid-free-gifts-add-on-offer-12kogqa/',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          FAQ
                        </Button>
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-purple-300 bg-white text-purple-600 hover:bg-purple-50 hover:border-purple-400"
                          onClick={() =>
                            window.open(
                              'https://help.shipaid.com/en/article/how-to-incorporate-free-donation-gifts-into-your-messaging-3ht0a/',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          Showcase Donation Gifts
                        </Button>
                        <Button
                          variant="outlined"
                          size={SizeEnum.sm}
                          className="border-purple-300 bg-white text-purple-600 hover:bg-purple-50 hover:border-purple-400"
                          onClick={() =>
                            window.open(
                              'https://guides.shipaid.com/demo/cmgb2vynd3wyv2nomrzpxn5xj',
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          Watch Overview Video
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </section>

            {/* FAQ Section */}
            <div className="border-t pt-8 mt-10">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Frequently Asked Questions
                </h2>
              </div>
              <div className="space-y-0">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={index}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        className="w-full flex items-center justify-between py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-sm"
                        aria-expanded={isOpen}
                        aria-controls={`faq-content-${index}`}
                      >
                        <span className="text-sm font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </span>
                        <div className="flex-shrink-0">
                          {isOpen ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      </button>
                      {isOpen && (
                        <div id={`faq-content-${index}`} className="pb-4 pt-0">
                          <p className="text-sm text-gray-700">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Email Preview Modal */}
        <Modal
          open={showEmailPreviewModal}
          openModal={(value) => setShowEmailPreviewModal(value)}
          containerClass="sm:max-w-4xl sm:w-full shadow-lg"
        >
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Customer Email Preview
              </h3>
              <p className="text-sm text-gray-600">
                This is how the customer email will look which will be sent
                after their purchase.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-4">
              <img
                src="/inspired.png"
                alt="Customer email preview"
                className="w-full h-auto"
              />
            </div>

            <div className="flex justify-end">
              <Button
                variant="outlined"
                size={SizeEnum.sm}
                className="border-pink-300 bg-white text-pink-600 hover:bg-pink-50 hover:border-pink-400"
                onClick={() =>
                  window.open(
                    'https://guides.shipaid.com/demo/cmgb2vynd3wyv2nomrzpxn5xj',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                See What Happens Next →
              </Button>
            </div>
          </div>
        </Modal>
      </SettingsViewWrapper>
    );
  },
);

export default Impact;
